import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Clock, Link2, Users, Loader2, X, Search, Building2, Image, Upload } from "lucide-react";
import { toast } from "sonner";
import { safeFetchJson } from "@/utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Event types available per role
const EVENT_TYPES = {
  enterprise: [
    { value: "audition", label: "Audition" },
    { value: "meeting", label: "Meeting" },
    { value: "callback", label: "Callback" },
    { value: "workshop", label: "Workshop" },
    { value: "live_class", label: "Live Class" }
  ],
  trainer: [
    { value: "workshop", label: "Workshop" },
    { value: "live_class", label: "Live Class" }
  ]
};

export default function CreateEventModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  userRole = "enterprise", 
  editEvent = null,
  initialValues = null,  // Pre-fill form with initial values (e.g., for Interview Call)
  initialParticipants = []  // Pre-selected participants
}) {
  const [loading, setLoading] = useState(false);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const posterInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    event_type: "",
    date: "",
    time: "",
    duration_minutes: 60,
    meeting_link: "",
    visibility_scope: "private",
    participant_user_ids: [],
    facilitator_trainer_ids: [],
    partner_enterprise_id: "",
    poster_url: ""
  });
  
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [selectedFacilitators, setSelectedFacilitators] = useState([]);

  // Reset form when modal opens (only depends on isOpen to prevent infinite loops)
  useEffect(() => {
    if (isOpen) {
      if (editEvent) {
        // Populate form for editing
        const eventDate = new Date(editEvent.start_at);
        setFormData({
          title: editEvent.title || "",
          description: editEvent.description || "",
          event_type: editEvent.event_type || "",
          date: eventDate.toISOString().split("T")[0],
          time: eventDate.toTimeString().slice(0, 5),
          duration_minutes: editEvent.duration_minutes || 60,
          meeting_link: editEvent.meeting_link || "",
          visibility_scope: editEvent.visibility_scope || "private",
          participant_user_ids: [],
          facilitator_trainer_ids: [],
          partner_enterprise_id: editEvent.partner_enterprise_id || "",
          poster_url: editEvent.poster_url || ""
        });
        setSelectedParticipants([]);
        setSelectedFacilitators([]);
      } else {
        // Reset for new event with optional initial values
        const defaultValues = {
          title: "",
          description: "",
          event_type: "",
          date: "",
          time: "",
          duration_minutes: 60,
          meeting_link: "",
          visibility_scope: "private",
          participant_user_ids: [],
          facilitator_trainer_ids: [],
          partner_enterprise_id: "",
          poster_url: ""
        };
        
        // Merge initial values if provided (for Interview Call, etc.)
        const mergedValues = initialValues 
          ? { ...defaultValues, ...initialValues, participant_user_ids: initialParticipants?.map(p => p.user_id) || [] }
          : defaultValues;
          
        setFormData(mergedValues);
        setSelectedParticipants(initialParticipants || []);
        setSelectedFacilitators([]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const searchUsers = async (query, roleFilter = null) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchingUsers(true);
    try {
      const token = localStorage.getItem("token");
      const roleParam = roleFilter ? `&role=${roleFilter}` : "";
      const parsed = await safeFetchJson(`${API_URL}/api/events/search-users?q=${encodeURIComponent(query)}${roleParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (parsed.ok) {
        setSearchResults(parsed.data || []);
      }
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      setSearchingUsers(false);
    }
  };

  const addParticipant = (user) => {
    if (!selectedParticipants.find(p => p.user_id === user.user_id)) {
      setSelectedParticipants(prev => [...prev, user]);
      setFormData(prev => ({
        ...prev,
        participant_user_ids: [...prev.participant_user_ids, user.user_id]
      }));
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  const removeParticipant = (userId) => {
    setSelectedParticipants(prev => prev.filter(p => p.user_id !== userId));
    setFormData(prev => ({
      ...prev,
      participant_user_ids: prev.participant_user_ids.filter(id => id !== userId)
    }));
  };

  const addFacilitator = (user) => {
    if (!selectedFacilitators.find(f => f.user_id === user.user_id)) {
      setSelectedFacilitators(prev => [...prev, user]);
      setFormData(prev => ({
        ...prev,
        facilitator_trainer_ids: [...prev.facilitator_trainer_ids, user.user_id]
      }));
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  const removeFacilitator = (userId) => {
    setSelectedFacilitators(prev => prev.filter(f => f.user_id !== userId));
    setFormData(prev => ({
      ...prev,
      facilitator_trainer_ids: prev.facilitator_trainer_ids.filter(id => id !== userId)
    }));
  };

  // Handle poster image upload
  const handlePosterUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingPoster(true);
    try {
      const token = localStorage.getItem("token");
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const response = await fetch(`${API_URL}/api/upload/image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formDataUpload
      });

      if (!response.ok) {
        throw new Error("Failed to upload image");
      }

      const data = await response.json();
      handleInputChange("poster_url", data.url);
      toast.success("Poster uploaded!");
    } catch (error) {
      toast.error(error.message || "Failed to upload poster");
    } finally {
      setUploadingPoster(false);
    }
  };

  const removePoster = () => {
    handleInputChange("poster_url", "");
    if (posterInputRef.current) {
      posterInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      toast.error("Please enter an event title");
      return;
    }
    if (!formData.event_type) {
      toast.error("Please select an event type");
      return;
    }
    if (!formData.date || !formData.time) {
      toast.error("Please select date and time");
      return;
    }
    if (!formData.meeting_link.trim() || !formData.meeting_link.startsWith("http")) {
      toast.error("Please enter a valid meeting link (URL)");
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      
      // Combine date and time into ISO string
      const startAt = new Date(`${formData.date}T${formData.time}:00`).toISOString();
      
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        event_type: formData.event_type,
        start_at: startAt,
        duration_minutes: parseInt(formData.duration_minutes),
        meeting_link: formData.meeting_link.trim(),
        visibility_scope: formData.visibility_scope,
        participant_user_ids: formData.participant_user_ids,
        facilitator_trainer_ids: formData.facilitator_trainer_ids,
        poster_url: formData.poster_url || null
      };
      
      if (userRole === "trainer" && formData.partner_enterprise_id) {
        payload.partner_enterprise_id = formData.partner_enterprise_id;
      }
      
      const url = editEvent 
        ? `${API_URL}/api/events/${editEvent.event_id}`
        : `${API_URL}/api/events`;
      
      const method = editEvent ? "PUT" : "POST";
      
      const parsed = await safeFetchJson(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!parsed.ok) {
        throw new Error(parsed.data?.detail || "Failed to save event");
      }
      
      toast.success(editEvent ? "Event updated successfully" : "Event created successfully");
      onSuccess?.(parsed.data);
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const eventTypes = EVENT_TYPES[userRole] || EVENT_TYPES.enterprise;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-event-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {editEvent ? "Edit Event" : "Create New Event"}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          {/* Event Type */}
          <div className="space-y-2">
            <Label htmlFor="event_type">Event Type *</Label>
            <Select 
              value={formData.event_type} 
              onValueChange={(v) => handleInputChange("event_type", v)}
            >
              <SelectTrigger data-testid="event-type-select">
                <SelectValue placeholder="Select event type" />
              </SelectTrigger>
              <SelectContent>
                {eventTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              placeholder="e.g., Audition for Lead Role"
              data-testid="event-title-input"
            />
          </div>
          
          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="Event details..."
              rows={3}
              data-testid="event-description-input"
            />
          </div>
          
          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Date *
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange("date", e.target.value)}
                data-testid="event-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="time" className="flex items-center gap-2">
                <Clock className="w-4 h-4" /> Time *
              </Label>
              <Input
                id="time"
                type="time"
                value={formData.time}
                onChange={(e) => handleInputChange("time", e.target.value)}
                data-testid="event-time-input"
              />
            </div>
          </div>
          
          {/* Duration */}
          <div className="space-y-2">
            <Label htmlFor="duration">Duration (minutes) *</Label>
            <Select 
              value={String(formData.duration_minutes)} 
              onValueChange={(v) => handleInputChange("duration_minutes", parseInt(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1.5 hours</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
                <SelectItem value="180">3 hours</SelectItem>
                <SelectItem value="240">4 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Meeting Link */}
          <div className="space-y-2">
            <Label htmlFor="meeting_link" className="flex items-center gap-2">
              <Link2 className="w-4 h-4" /> Meeting Link *
            </Label>
            <Input
              id="meeting_link"
              value={formData.meeting_link}
              onChange={(e) => handleInputChange("meeting_link", e.target.value)}
              placeholder="https://zoom.us/j/... or https://meet.google.com/..."
              data-testid="event-meeting-link-input"
            />
            <p className="text-xs text-gray-500">
              Paste your Zoom, Google Meet, or other video conferencing link
            </p>
          </div>
          
          {/* Visibility */}
          <div className="space-y-2">
            <Label>Visibility</Label>
            <Select 
              value={formData.visibility_scope} 
              onValueChange={(v) => handleInputChange("visibility_scope", v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Private (Invite only)</SelectItem>
                <SelectItem value="public">Public (Anyone can register)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Event Poster Image (Optional) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Image className="w-4 h-4" /> Event Poster (Optional)
            </Label>
            {formData.poster_url ? (
              <div className="relative">
                <img 
                  src={formData.poster_url} 
                  alt="Event poster preview" 
                  className="w-full h-32 object-cover rounded-lg border"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={removePoster}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div 
                className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center cursor-pointer hover:border-violet-300 transition-colors"
                onClick={() => posterInputRef.current?.click()}
              >
                {uploadingPoster ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-violet-600" />
                    <span className="text-sm text-gray-600">Uploading...</span>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload poster image</p>
                    <p className="text-xs text-gray-400 mt-1">Max 5MB, JPG/PNG recommended</p>
                  </>
                )}
              </div>
            )}
            <input
              ref={posterInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePosterUpload}
            />
          </div>
          
          {/* Invite Participants (only for private events) */}
          {formData.visibility_scope === "private" && !editEvent && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" /> Invite Participants
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    searchUsers(e.target.value);
                  }}
                  placeholder="Search by name or email..."
                  className="pl-10"
                />
                {searchingUsers && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" />
                )}
              </div>
              
              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="border rounded-lg max-h-40 overflow-y-auto">
                  {searchResults.map(user => (
                    <div
                      key={user.user_id}
                      className="p-2 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                      onClick={() => addParticipant(user)}
                    >
                      <div>
                        <p className="text-sm font-medium">{user.display_name || user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                      <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{user.role}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Selected Participants */}
              {selectedParticipants.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedParticipants.map(user => (
                    <span 
                      key={user.user_id}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-violet-100 text-violet-800 rounded-full text-sm"
                    >
                      {user.display_name || user.name}
                      <X 
                        className="w-3 h-3 cursor-pointer hover:text-violet-600" 
                        onClick={() => removeParticipant(user.user_id)}
                      />
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Add Facilitators (Trainers) */}
          {!editEvent && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" /> Add Facilitators (Trainers)
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search trainers..."
                  className="pl-10"
                  onChange={(e) => searchUsers(e.target.value, "trainer")}
                />
              </div>
              
              {/* Search Results for Trainers */}
              {searchResults.length > 0 && searchResults[0]?.role === "trainer" && (
                <div className="border rounded-lg max-h-40 overflow-y-auto">
                  {searchResults.filter(u => u.role === "trainer").map(user => (
                    <div
                      key={user.user_id}
                      className="p-2 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                      onClick={() => addFacilitator(user)}
                    >
                      <div>
                        <p className="text-sm font-medium">{user.display_name || user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Selected Facilitators */}
              {selectedFacilitators.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedFacilitators.map(user => (
                    <span 
                      key={user.user_id}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                    >
                      {user.display_name || user.name}
                      <X 
                        className="w-3 h-3 cursor-pointer hover:text-blue-600" 
                        onClick={() => removeFacilitator(user.user_id)}
                      />
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Partner Enterprise (only for trainers) */}
          {userRole === "trainer" && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Partner Enterprise (Optional)
              </Label>
              <Input
                value={formData.partner_enterprise_id}
                onChange={(e) => handleInputChange("partner_enterprise_id", e.target.value)}
                placeholder="Enterprise ID (optional)"
              />
              <p className="text-xs text-gray-500">
                Add an enterprise partner for collaboration
              </p>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="create-event-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {editEvent ? "Updating..." : "Creating..."}
                </>
              ) : (
                editEvent ? "Update Event" : "Create Event"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
